type ButtonState = 'idle' | 'hover' | 'pressed'

export const PlayButtonLight: React.FC<{ state: ButtonState }> = ({ state }) => {
  // Idle: white/60 bg. Hover: transitions to Background/1 (more opaque white). Pressed: scale down.
  const bgOpacity = state === 'hover' ? 0.9 : 0.6
  const scale = state === 'pressed' ? 0.93 : 1

  // Border color: Border/4 for light mode (F1F1F1), subtle opacity change on hover
  const borderOpacity = state === 'hover' ? 1 : 1

  return (
    <svg
      width="214"
      height="214"
      viewBox="0 0 214 214"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: `scale(${scale})`,
        transition: 'transform 0.12s ease',
        cursor: 'pointer',
        filter:
          state === 'hover' ? 'drop-shadow(0 4px 16px rgba(222,0,0,0.35))' : 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))',
      }}
    >
      {/* Backdrop blur via foreignObject (mirrors Figma source) */}
      <foreignObject x="-20" y="-20" width="254" height="254">
        <div
          style={{
            backdropFilter: 'blur(10px)',
            clipPath: 'url(#bgblur_light_clip_path)',
            height: '100%',
            width: '100%',
          }}
        />
      </foreignObject>

      <g clipPath="url(#clipLight)">
        {/* Background panel — white/60 idle, white/90 hover */}
        <path
          d="M0 24C0 10.7452 10.7452 0 24 0H190C203.255 0 214 10.7452 214 24V190C214 203.255 203.255 214 190 214H24C10.7452 214 0 203.255 0 190V24Z"
          fill="white"
          fillOpacity={bgOpacity}
          style={{ transition: 'fill-opacity 0.9s ease' }}
        />

        {/* Border — Border/4 for light mode: #F1F1F1, 2px */}
        <path
          d="M24 0V2H190V0V-2H24V0ZM214 24H212V190H214H216V24H214ZM190 214V212H24V214V216H190V214ZM0 190H2V24H0H-2V190H0ZM24 214V212C11.8497 212 2 202.15 2 190H0H-2C-2 204.359 9.6406 216 24 216V214ZM214 190H212C212 202.15 202.15 212 190 212V214V216C204.359 216 216 204.359 216 190H214ZM190 0V2C202.15 2 212 11.8497 212 24H214H216C216 9.6406 204.359 -2 190 -2V0ZM24 0V-2C9.6406 -2 -2 9.6406 -2 24H0H2C2 11.8497 11.8497 2 24 2V0Z"
          fill="#F1F1F1"
          fillOpacity={borderOpacity}
          clipPath="url(#clipLight)"
          style={{ transition: 'fill-opacity 0.2s ease' }}
        />

        {/* Waveform bars */}
        <g opacity="0.2">
          {[
            [8, 61, 5, 60],
            [38, 54, 5, 74],
            [23, 69, 5, 44],
            [53, 81, 5, 20],
            [82, 54, 5, 74],
            [68, 69, 4, 44],
            [97, 81, 5, 20],
            [127, 54, 5, 74],
            [112, 69, 5, 44],
            [142, 81, 4, 20],
            [156, 54, 5, 74],
            [186, 61, 5, 60],
            [171, 69, 5, 44],
            [201, 69, 5, 44],
          ].map(([x, y, w, h], i) => (
            <rect key={i} x={x} y={y} width={w} height={h} rx={w / 2} fill="#F70000" />
          ))}
        </g>

        {/* Red circle with drop shadow */}
        <g filter="url(#filterLight1)">
          <rect x="47" y="31" width="120" height="120" rx="60" fill="#DE0000" />
        </g>

        {/* Ring 1 */}
        <g filter="url(#filterLight2)">
          <rect
            x="57.5"
            y="41.5"
            width="99"
            height="99"
            rx="49.5"
            stroke="white"
            strokeOpacity="0.15"
            shapeRendering="crispEdges"
          />
        </g>

        {/* Ring 2 */}
        <g filter="url(#filterLight3)">
          <rect
            x="68.5"
            y="52.5"
            width="77"
            height="77"
            rx="38.5"
            stroke="white"
            strokeOpacity="0.15"
            shapeRendering="crispEdges"
          />
        </g>

        {/* Inner white circle with black border */}
        <mask id="innerCircleLightMask" fill="white">
          <path d="M75.0018 91C75.0018 73.3269 89.3287 59 107.002 59C124.675 59 139.002 73.3269 139.002 91C139.002 108.673 124.675 123 107.002 123C89.3287 123 75.0018 108.673 75.0018 91Z" />
        </mask>
        <path
          d="M75.0018 91C75.0018 73.3269 89.3287 59 107.002 59C124.675 59 139.002 73.3269 139.002 91C139.002 108.673 124.675 123 107.002 123C89.3287 123 75.0018 108.673 75.0018 91Z"
          fill={`${state === 'hover' ? '#D4D4D4' : 'white'}`}
        />
        {/* Black 2px border on inner circle */}
        <path
          d="M75.0018 91M139.002 91M139.002 91M75.0018 91M107.002 59M139.002 91M107.002 123M75.0018 91M107.002 123V121C90.4332 121 77.0018 107.569 77.0018 91H75.0018H73.0018C73.0018 109.778 88.2241 125 107.002 125V123ZM139.002 91H137.002C137.002 107.569 123.57 121 107.002 121V123V125C125.779 125 141.002 109.778 141.002 91H139.002ZM107.002 59V61C123.57 61 137.002 74.4315 137.002 91H139.002H141.002C141.002 72.2223 125.779 57 107.002 57V59ZM107.002 59V57C88.2241 57 73.0018 72.2223 73.0018 91H75.0018H77.0018C77.0018 74.4315 90.4332 61 107.002 61V59Z"
          fill="black"
          mask="url(#innerCircleLightMask)"
        />

        {/* Arrow icon — black for light mode */}
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M111.752 90.3332L107.585 86.1665C107.252 85.8332 106.752 85.8332 106.418 86.1665C106.085 86.4998 106.085 86.9998 106.418 87.3332L109.168 90.0832H102.835C102.335 90.0832 102.002 90.4165 102.002 90.9165C102.002 91.4165 102.335 91.7498 102.835 91.7498H109.168L106.418 94.4998C106.085 94.8332 106.085 95.3332 106.418 95.6665C106.752 95.9998 107.252 95.9998 107.585 95.6665L111.752 91.4998C112.085 91.2498 112.085 90.6665 111.752 90.3332Z"
          fill="black"
        />

        {/* Label text — COKE STUDIO PLAYLIST in dark gray for light mode */}
        <svg
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          textAnchor="middle"
          width="12"
          x="20"
          y="185"
        >
          <path
            d="M6 0C9.31177 0.000163236 11.9989 2.68822 11.999 6C11.999 9.31188 9.31185 11.9998 6 12C2.68801 12 0 9.31199 0 6C0.000128267 2.68812 2.68809 0 6 0ZM8.57031 8.05664C6.84301 7.02378 4.81819 6.98499 2.83691 7.42285C2.65556 7.46159 2.50788 7.63323 2.50781 7.8291C2.50781 8.02506 2.65596 8.21191 2.89062 8.21191C2.96077 8.21177 3.0836 8.17259 3.17773 8.14844C4.91232 7.79281 6.6691 7.83161 8.17871 8.74121C8.27302 8.79683 8.33597 8.8281 8.4375 8.82812C8.6334 8.82812 8.80555 8.67609 8.80566 8.45605C8.80566 8.2359 8.71789 8.15099 8.57031 8.05664ZM5.00195 5.19238C3.9957 5.19241 3.30823 5.3328 2.63574 5.52148C2.38689 5.59173 2.26074 5.76377 2.26074 6.02246C2.26097 6.2811 2.47179 6.49116 2.73047 6.49121C2.83934 6.49121 2.90243 6.46016 3.01855 6.42871C5.02412 5.88439 7.27898 6.24452 8.79102 7.13965C8.87808 7.18561 8.96311 7.24121 9.08887 7.24121C9.35235 7.24097 9.5552 7.03066 9.55762 6.76953C9.5576 6.55909 9.48012 6.40908 9.28418 6.29297C8.08908 5.58176 6.57199 5.19238 5.00195 5.19238ZM5.11816 3.13086C4.08038 3.13088 3.12691 3.24986 2.27539 3.49902C2.05781 3.56205 1.85449 3.75558 1.85449 4.07715C1.85459 4.39881 2.09967 4.64746 2.41895 4.64746C2.53486 4.64736 2.64351 4.60912 2.73047 4.58496C4.72395 4.02853 7.80384 4.27552 9.52637 5.30371C9.63517 5.36656 9.71312 5.39746 9.83887 5.39746C10.1338 5.39716 10.3994 5.12172 10.3994 4.83398C10.3994 4.54627 10.2765 4.39595 10.0879 4.28711C8.73552 3.49843 6.88423 3.13086 5.11816 3.13086Z"
            fill="#505050"
          />
        </svg>
        <text x="117" y="196" textAnchor="middle" className="subtitle-xs text-[#505050]" fill="#505050">
          COKE STUDIO PLAYLIST
        </text>
      </g>

      <defs>
        <clipPath id="bgblur_light_clip_path" transform="translate(20 20)">
          <path d="M0 24C0 10.7452 10.7452 0 24 0H190C203.255 0 214 10.7452 214 24V190C214 203.255 203.255 214 190 214H24C10.7452 214 0 203.255 0 190V24Z" />
        </clipPath>
        <filter
          id="filterLight1"
          x="32"
          y="16"
          width="150"
          height="150"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter
          id="filterLight2"
          x="42"
          y="26"
          width="130"
          height="130"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter
          id="filterLight3"
          x="53"
          y="37"
          width="108"
          height="108"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <clipPath id="clipLight">
          <path
            d="M0 24C0 10.7452 10.7452 0 24 0H190C203.255 0 214 10.7452 214 24V190C214 203.255 203.255 214 190 214H24C10.7452 214 0 203.255 0 190V24Z"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  )
}
